import { useCallback, useState } from 'react';
import { useAnalyticsContext } from '../context/analytics-context';
import { ImageResponse } from '../global-types';
import { deletePhotoTrayImages } from '../services';

export const useDeletePhotoTrayImages = (
  setPhotoTrayImages: React.Dispatch<React.SetStateAction<ImageResponse[]>>,
  handleDeleteImageLocally: (photoId: string) => void,
  setIsDeleteMode: (isDeleteMode: boolean) => void,
) => {
  const [imagesToDelete, setImagesToDelete] = useState<ImageResponse[]>([]);
  const [deleteImagesConfirmationOpen, setDeleteImagesConfirmationOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { trackDeletePhotoTrayPhotos } = useAnalyticsContext();

  /**
   * Calls the API to delete the photoTrayImages
   * @param photoTrayIds array of string containing the photoTrayImageIds
   * @returns Promise<void> and a 204 empty response
   */
  const handleDeletePhotoTrayImages = async (photoTrayIds: string[]) => {
    try {
      await deletePhotoTrayImages(photoTrayIds);
      photoTrayIds.forEach(handleDeleteImageLocally);
    } catch (error) {
      throw new Error(error as any);
    } finally {
      // Send the number of delete photo tray photos to adobe if above logic succeeds
      trackDeletePhotoTrayPhotos(photoTrayIds.length);
    }
  };

  // Sets the delete confirmation dialog open/close state
  const handleOpenDeleteImagesConfirmationDialog = (isOpen: boolean) => {
    setDeleteImagesConfirmationOpen(isOpen);
  };

  const handleDeleteImagesConfirmation = useCallback(async () => {
    setIsDeleting(true);
    // Close the delete images confirmation dialog
    handleOpenDeleteImagesConfirmationDialog(false);
    // Remove the images to delete from the photoTrayImages state
    setPhotoTrayImages((prevImages) => prevImages.filter((photo) => !imagesToDelete.includes(photo)));

    // Maps the photoTrayId from each image receive in imagesToDelete param
    const imagesToDeleteIds = imagesToDelete.map((image) => image.photo_tray_image_id);
    // Calls the function to delete the images
    await handleDeletePhotoTrayImages(imagesToDeleteIds);

    // Reset the imagesToDelete state
    setImagesToDelete([]);
    setIsDeleteMode(false);
    setIsDeleting(false);
  }, [
    imagesToDelete,
    setPhotoTrayImages,
    setImagesToDelete,
    handleOpenDeleteImagesConfirmationDialog,
    deletePhotoTrayImages,
  ]);

  return {
    setImagesToDelete,
    deleteImagesConfirmationOpen,
    handleDeleteImagesConfirmation,
    handleOpenDeleteImagesConfirmationDialog,
    isDeleting,
  };
};
